import {css, StyleSheet} from 'aphrodite';
import {produce} from 'immer';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import SaveLocationPicker, {
  DEFAULT_WORKLIST_PARAMS,
  LocationInformation,
} from 'molbio/components/SaveLocationPicker';
import {GRAY_DARKER} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {InputChangeEvent} from 'util/Types';
import {WorklistFormType} from 'worklists/WorklistConstants';

interface SaveEntitiesToggleProps {
  checkboxLabel: string;
  checkboxTooltip: string;
  saveEntities: boolean;
  onUpdateSaveEntities: (saveEntities: boolean) => void;
  uniqueId: string;
}

function SaveEntitiesToggle({
  checkboxLabel,
  checkboxTooltip,
  saveEntities,
  onUpdateSaveEntities,
  uniqueId,
}: SaveEntitiesToggleProps): JSX.Element {
  return (
    <>
      <div className={css(styles.checkboxWrapper)}>
        <CheckboxInput
          checked={saveEntities}
          id={`save-entities-checkbox-${uniqueId}`}
          label={checkboxLabel}
          onChange={(e) => {
            onUpdateSaveEntities(e.target.checked);
          }}
        />
        <div className={css(styles.checkboxTooltip)}>
          <Tooltip text={checkboxTooltip}>
            <BenchlingIcon icon="info-circle" className={css(styles.checkboxIcon)} />
          </Tooltip>
        </div>
      </div>
    </>
  );
}

interface SaveFragmentsAndPrimersStep {
  primerLocationInfo: LocationInformation;
  onUpdatePrimerLocationInfo: (locationInfo: LocationInformation) => void;
  fragmentLocationInfo: LocationInformation;
  onUpdateFragmentLocationInfo: (locationInfo: LocationInformation) => void;
  newWorklistOwnerId: string;
  shouldShowOverflowWarningForNewWorklist: boolean;
  shouldShowOverflowWarningForExistingWorklist: boolean;
  shouldSaveFragments: boolean;
  setShouldSaveFragments: (saveFragments: boolean) => void;
  shouldSavePrimers: boolean;
  setShouldSavePrimers: (savePrimers: boolean) => void;
  canSavePrimers: boolean;
}

export function SaveFragmentsAndPrimersStep(props: SaveFragmentsAndPrimersStep): JSX.Element {
  const sharedWorklistParams = {
    newWorklistOwnerId: props.newWorklistOwnerId,
    shouldShowOverflowWarningForNewWorklist: props.shouldShowOverflowWarningForNewWorklist,
    shouldShowOverflowWarningForExistingWorklist: props.shouldShowOverflowWarningForExistingWorklist,
  };
  const onWorklistFormTypeChange = (
    newOrExisting: WorklistFormType,
    locationInfo: LocationInformation,
    onUpdateLocationInfo: (locationInfo: LocationInformation) => void
  ): void => {
    if (newOrExisting === WorklistFormType.NEW) {
      onUpdateLocationInfo(
        produce(locationInfo, (draft) => {
          draft.worklistParams!.targetWorklistId = null;
          draft.worklistParams!.worklistFormType = WorklistFormType.NEW;
        })
      );
    } else {
      onUpdateLocationInfo(
        produce(locationInfo, (draft) => {
          draft.worklistParams!.newWorklistInfo = null;
          draft.worklistParams!.worklistFormType = WorklistFormType.EXISTING;
        })
      );
    }
  };
  const onNewWorklistNameChange = (
    e: InputChangeEvent,
    locationInfo: LocationInformation,
    onUpdateLocationInfo: (locationInfo: LocationInformation) => void
  ): void => {
    onUpdateLocationInfo(
      produce(locationInfo, (draft) => {
        if (e.target.value) {
          draft.worklistParams!.newWorklistInfo = {name: e.target.value};
        } else {
          draft.worklistParams!.newWorklistInfo = null;
        }
      })
    );
  };
  return (
    <>
      {`Optionally save fragments${props.canSavePrimers ? ' and primers' : ''}`}
      <SaveEntitiesToggle
        checkboxLabel="Create DNA Sequences to represent amplified fragments"
        checkboxTooltip="If you deselect this option, Benchling will not create any new amplified fragments as part of this assembly"
        saveEntities={props.shouldSaveFragments}
        onUpdateSaveEntities={props.setShouldSaveFragments}
        uniqueId="fragments"
      />
      {props.shouldSaveFragments && (
        <SaveLocationPicker
          targetFolderId={props.fragmentLocationInfo.targetFolderId}
          onTargetFolderIdChange={(e) => {
            props.onUpdateFragmentLocationInfo({
              ...props.fragmentLocationInfo,
              targetFolderId: e.target.value,
            });
          }}
          tagSchemaInfo={{
            tagSchemaId: props.fragmentLocationInfo.tagSchemaId,
            onUpdateTagSchemaId: (tagSchemaId) => {
              props.onUpdateFragmentLocationInfo({...props.fragmentLocationInfo, tagSchemaId});
            },
            oligoAwareFolderItemTypes: [ModelTypeConstants.SEQUENCE],
          }}
          worklistFormType={
            props.fragmentLocationInfo.worklistParams?.worklistFormType ?? WorklistFormType.NEW
          }
          onWorklistFormTypeChange={(newOrExisting) => {
            onWorklistFormTypeChange(
              newOrExisting,
              props.fragmentLocationInfo,
              props.onUpdateFragmentLocationInfo
            );
          }}
          newWorklistName={props.fragmentLocationInfo.worklistParams?.newWorklistInfo?.name ?? ''}
          onNewWorklistNameChange={(e) => {
            onNewWorklistNameChange(e, props.fragmentLocationInfo, props.onUpdateFragmentLocationInfo);
          }}
          onWorklistIdChange={(targetWorklist) => {
            props.onUpdateFragmentLocationInfo(
              produce(props.fragmentLocationInfo, (draft) => {
                draft.worklistParams!.targetWorklistId = targetWorklist == null ? null : targetWorklist.id;
              })
            );
          }}
          worklistParams={{
            ...sharedWorklistParams,
            useWorklistCheckboxLabel: 'Add amplified fragments to a worklist',
            pluralEntityLabel: 'fragments',
          }}
          required={true}
          useWorklist={props.fragmentLocationInfo.worklistParams != null}
          uniqueId="redesign-fragments"
          onUseWorklistChange={(useWorklist: boolean) => {
            props.onUpdateFragmentLocationInfo(
              produce(props.fragmentLocationInfo, (draft) => {
                draft.worklistParams = useWorklist ? DEFAULT_WORKLIST_PARAMS : null;
              })
            );
          }}
        />
      )}
      {props.canSavePrimers && (
        <SaveEntitiesToggle
          checkboxLabel="Create DNA Oligos to represent newly designed primers"
          checkboxTooltip="If you deselect this option, Benchling will not create any new primers as part of this assembly"
          saveEntities={props.shouldSavePrimers}
          onUpdateSaveEntities={props.setShouldSavePrimers}
          uniqueId="primers"
        />
      )}
      {props.canSavePrimers && props.shouldSavePrimers && (
        <SaveLocationPicker
          targetFolderId={props.primerLocationInfo.targetFolderId}
          onTargetFolderIdChange={(e) => {
            props.onUpdatePrimerLocationInfo({...props.primerLocationInfo, targetFolderId: e.target.value});
          }}
          tagSchemaInfo={{
            tagSchemaId: props.primerLocationInfo.tagSchemaId,
            onUpdateTagSchemaId: (tagSchemaId) => {
              props.onUpdatePrimerLocationInfo({...props.primerLocationInfo, tagSchemaId});
            },
            oligoAwareFolderItemTypes: [ModelTypeConstants.DNA_OLIGO],
          }}
          worklistFormType={props.primerLocationInfo.worklistParams?.worklistFormType ?? WorklistFormType.NEW}
          onWorklistFormTypeChange={(newOrExisting) => {
            onWorklistFormTypeChange(
              newOrExisting,
              props.primerLocationInfo,
              props.onUpdatePrimerLocationInfo
            );
          }}
          newWorklistName={props.primerLocationInfo.worklistParams?.newWorklistInfo?.name ?? ''}
          onNewWorklistNameChange={(e) => {
            onNewWorklistNameChange(e, props.primerLocationInfo, props.onUpdatePrimerLocationInfo);
          }}
          onWorklistIdChange={(targetWorklist) => {
            props.onUpdatePrimerLocationInfo(
              produce(props.primerLocationInfo, (draft) => {
                draft.worklistParams!.targetWorklistId = targetWorklist == null ? null : targetWorklist.id;
              })
            );
          }}
          worklistParams={{
            ...sharedWorklistParams,
            useWorklistCheckboxLabel: 'Add primers to a worklist',
            pluralEntityLabel: 'primers',
          }}
          required={true}
          useWorklist={props.primerLocationInfo.worklistParams != null}
          uniqueId="redesign-primers"
          onUseWorklistChange={(useWorklist: boolean) => {
            props.onUpdatePrimerLocationInfo(
              produce(props.primerLocationInfo, (draft) => {
                draft.worklistParams = useWorklist ? DEFAULT_WORKLIST_PARAMS : null;
              })
            );
          }}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  checkboxWrapper: {marginTop: 16, marginBottom: 16, display: 'flex'},
  checkboxIcon: {color: GRAY_DARKER, width: '16px', height: '16px'},
  checkboxTooltip: {marginLeft: 8},
});
